import React from 'react';
import { ChevronDown } from 'lucide-react';
import { GenericTable, GenericTableColumn } from '../../../../ui-components/Table/GenericTable';
import { GenericTag } from '../../../../ui-components/Tag/GenericTag';
import { formatClusterRole } from '../helpers/bgpVisibilityApi';
import { formatTagAttribution } from '../../../helpers/bgpValidation';
import { RouteRow, RouteRowGroup, groupRoutesByPrefix } from '../utils/routeModel';
import { routeFamilyLabel } from '../BgpVisibilityData';
import { decisionLabel } from '../utils/decisionReason';
import {
  CLOUD_HEALTH_SEVERITY,
  CloudRouteHealth,
  cloudErrorOf,
  deriveCloudRouteHealth,
  describeNextHopIssue,
  worstCloudRouteHealth
} from '../utils/cloudRouteHealth';
import { MultiChipCell } from './MultiChipCell';

interface BgpRouteTableProps {
  rows: RouteRow[];
  direction: 'INBOUND' | 'OUTBOUND';
  /** Whether the current entity is a cluster (adds the CTX Reflector column). */
  showReflectorColumn: boolean;
  loading?: boolean;
}

const formatAsPath = (asPath: number[]): string => (asPath.length > 0 ? asPath.join(' ') : '—');

const dash = (value: string | number | null | undefined): React.ReactNode =>
  value === null || value === undefined || value === '' ? (
    <span className="text-semantic-text-tertiary">—</span>
  ) : (
    <span>{value}</span>
  );

const parseWireDate = (value: string | number | null | undefined): Date | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const asNumber = typeof value === 'number' ? value : Number(value);
  if (!Number.isNaN(asNumber)) {
    // Epoch seconds (what naboo sends) vs milliseconds.
    const date = new Date(asNumber * (asNumber < 1e12 ? 1000 : 1));
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatReceivedAt = (value: string | number | null): React.ReactNode => {
  const date = parseWireDate(value);
  if (!date) {
    return <span className="text-semantic-text-tertiary">—</span>;
  }
  return <span>{date.toLocaleString()}</span>;
};

/** Distinct, non-empty values in first-seen order. */
const dedupeValues = (values: (string | null | undefined)[]): string[] =>
  Array.from(new Set(values.filter((v): v is string => !!v)));

/**
 * Distinct discovery tags across a set of rows, formatted `name : value` (CTX-129). A prefix can
 * reach several destinations, so a group may carry more than one tag — or none, when every
 * destination was pinned explicitly in the rule.
 */
const discoveryTagLabels = (rows: RouteRow[]): string[] =>
  dedupeValues(rows.flatMap((row) => formatTagAttribution(row.tagAttribution)));

/**
 * Distinct reflectors that discovered a prefix, in first-seen order — the provenance the cluster
 * "Reflector" column reports. Each is tagged with its HA role ("CTXR-930b8a · Primary") so the
 * operator can tell which member announced the route without cross-referencing the selector.
 * Falls back to the member id when the payload carried no name, and drops the suffix when the
 * backend reports no role.
 */
const reflectorsOf = (group: RouteRowGroup): string[] =>
  dedupeValues(
    group.rows.map((r) => {
      const name = r.reflectorName || r.reflectorId;
      const role = formatClusterRole(r.reflectorRole);
      return role ? `${name} · ${role}` : name;
    })
  );


/**
 * Tag columns (Family / Best / Cloud) center their chip: they are single-badge
 * cells sitting between two-line headers ("Neighbor ASN", "Local Pref"), and
 * left-hugging chips under those made the row read as ragged.
 */
const TagCell: React.FC<{ children: React.ReactNode; title?: string }> = ({ children, title }) => (
  <span className="flex justify-center" title={title}>
    {children}
  </span>
);

/**
 * The cloud outcome as a badge (CTX-323). The full explanation lives in the hover
 * title, not inline: this sits in a dense label/value grid where a sentence would
 * dwarf every other field.
 */
const CloudBadge: React.FC<{ health: CloudRouteHealth }> = ({ health }) =>
  health.state === 'unknown' ? (
    <span className="text-semantic-text-tertiary" title={health.detail}>—</span>
  ) : (
    <span title={health.detail}>
      <GenericTag label={health.label} variant={health.variant} size="small" />
    </span>
  );

/** Epoch seconds -> local string, matching the other timestamps in this panel. */
const cloudTime = (epochSeconds: number | null): string =>
  epochSeconds == null ? '—' : new Date(epochSeconds * 1000).toLocaleString();

/**
 * One route's full detail, used both for single-route groups and per-route inside an
 * expanded multi-route group — kept as its own block so nothing gets merged across routes
 * that share a prefix but differ by reflector, neighbor, family, or destination (scope C.1).
 */
const RouteDetailBlock: React.FC<{ row: RouteRow; direction: 'INBOUND' | 'OUTBOUND' }> = ({ row, direction }) => {
  // naboo reuses `injectionError` for the next-hop explanation (CTX-738); only a real AWS
  // error is labelled as one.
  const cloudError = cloudErrorOf(row);
  const nextHopIssue = describeNextHopIssue(row);
  return (
    <div className="grid grid-cols-2 gap-x-8 gap-y-1 py-2 text-xs text-semantic-text-secondary md:grid-cols-3">
      <div><span className="font-semibold">Direction:</span> {row.direction}</div>
      <div><span className="font-semibold">Neighbor IP:</span> {row.neighborIp}</div>
      <div><span className="font-semibold">AS Path:</span> {formatAsPath(row.asPath)}</div>
      <div><span className="font-semibold">Origin:</span> {row.origin || '—'}</div>
      <div><span className="font-semibold">RD:</span> {row.rd || '—'}</div>
      <div>
        <span className="font-semibold">RT:</span>{' '}
        {(Array.isArray(row.rt) ? row.rt.join(', ') : row.rt) || '—'}
      </div>
      <div><span className="font-semibold">Status:</span> {row.status || '—'}</div>
      <div><span className="font-semibold">Cloud:</span> <CloudBadge health={deriveCloudRouteHealth(row)} /></div>
      <div><span className="font-semibold">Written to AWS:</span> {cloudTime(row.injectionAt)}</div>
      <div>
        <span className="font-semibold">Checked in AWS:</span> {cloudTime(row.awsCheckedAt)}
        {deriveCloudRouteHealth(row).stale && (
          <span className="text-semantic-text-tertiary"> (before the last write)</span>
        )}
      </div>
      {cloudError && (
        <div className="col-span-2 md:col-span-3">
          <span className="font-semibold">Cloud error:</span> {cloudError}
        </div>
      )}
      {nextHopIssue && (
        <div className="col-span-2 md:col-span-3">
          <span className="font-semibold">Next hop:</span> {nextHopIssue}
        </div>
      )}
      <div><span className="font-semibold">Cluster:</span> {row.clusterName || row.clusterId || '—'}</div>
      <div><span className="font-semibold">{direction === 'OUTBOUND' ? 'Subnet' : 'Route Table'}:</span> {(direction === 'OUTBOUND' ? row.subnetId : row.routeTableId) || '—'}</div>
      <div>
        <span className="font-semibold">Tags:</span>{' '}
        {formatTagAttribution(row.tagAttribution).join(' · ') || '—'}
      </div>
      <div><span className="font-semibold">Withdraw:</span> {row.withdrawnNeighborIp || '—'}</div>
      <div>
        <span className="font-semibold">Received at:</span>{' '}
        {parseWireDate(row.receivedAt)?.toLocaleString() ?? '—'}
      </div>
      <div>
        <span className="font-semibold">Timestamp:</span>{' '}
        {row.timestamp ? new Date(row.timestamp * (row.timestamp < 1e12 ? 1000 : 1)).toLocaleString() : '—'}
      </div>
      <div className="col-span-2 md:col-span-3">
        <span className="font-semibold">Decision:</span>{' '}
        <span title={row.decisionReason || undefined}>{decisionLabel(row.decisionReason) ?? '—'}</span>
      </div>
    </div>
  );
};

/**
 * The distinct display values of one attribute across a prefix group's routes, in first-seen
 * order. Absent values normalize to the em-dash placeholder and count as their own distinct
 * value, so a group where only some routes carry the attribute says so rather than implying
 * every route agrees.
 */
const distinctDisplayValues = (values: (string | number | null | undefined)[]): string[] =>
  Array.from(new Set(values.map((v) => (v === null || v === undefined || v === '' ? '—' : String(v)))));

/**
 * One attribute of a prefix group. Routes in a group usually agree, and that case renders as
 * plain text exactly as before. When they disagree the cell *names* each distinct value instead
 * of reporting how many there are — `Origin: 2 values` told the operator a count when what they
 * needed was `IGP` and `INCOMPLETE`, and no other surface exposed them (CTX-322). Divergent
 * values render through `MultiChipCell`, which caps the strip and moves the rest into its "+N"
 * popover, so a pathological group cannot blow out the grid.
 */
const SharedValue: React.FC<{ values: (string | number | null | undefined)[] }> = ({ values }) => {
  const distinct = distinctDisplayValues(values);
  if (distinct.length <= 1) {
    return <span>{distinct[0] ?? '—'}</span>;
  }
  return <MultiChipCell values={distinct} maxVisible={3} />;
};

/**
 * The distinct notes a prefix group's routes carry (a cloud error, a next-hop explanation), each
 * with the destinations it applies to, in first-seen order. Routes that have nothing to say are
 * left out.
 */
const notesByText = (
  rows: RouteRow[],
  noteOf: (row: RouteRow) => string | null,
  destOf: (row: RouteRow) => string
): { text: string; dests: string[] }[] => {
  const byText = new Map<string, Set<string>>();
  for (const row of rows) {
    const text = noteOf(row);
    if (!text) continue;
    const dests = byText.get(text) ?? new Set<string>();
    dests.add(destOf(row));
    byText.set(text, dests);
  }
  return Array.from(byText, ([text, dests]) => ({ text, dests: Array.from(dests) }));
};

/**
 * Full-width notes in a prefix group's shared grid. `RouteDetailBlock` shows these for a single
 * route; a cluster prefix always has one route per member, so without this the group view showed
 * the raw decision reason and never said why (CTX-738). The destinations are named only when the
 * routes disagree, since one note for every route needs no qualifier.
 */
const GroupNotes: React.FC<{ label: string; notes: { text: string; dests: string[] }[] }> = ({ label, notes }) => {
  if (notes.length === 0) {
    return null;
  }
  return (
    <div className="col-span-2 md:col-span-4">
      <span className="font-semibold">{label}:</span>{' '}
      {notes.length === 1 ? (
        notes[0].text
      ) : (
        <ul className="mt-0.5 list-disc pl-4">
          {notes.map((note) => (
            <li key={note.text}>
              <span className="font-medium text-semantic-text-primary">{note.dests.join(', ')}</span>: {note.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** A `label: value(s)` pair in the group's shared-attribute grid. */
const SharedField: React.FC<{
  label: string;
  values: (string | number | null | undefined)[];
  className?: string;
  /** Hover text for the field, e.g. the raw codes behind translated values. */
  title?: string;
}> = ({ label, values, className, title }) => (
  <div className={`flex flex-wrap items-center gap-x-1 ${className ?? ''}`} title={title}>
    <span className="font-semibold">{label}:</span>
    <SharedValue values={values} />
  </div>
);

/**
 * Consolidated detail for a prefix announced by multiple routes: the attributes that are the
 * same across every route (direction, family, origin, status, cluster, RD/RT, decision,
 * received-at) are shown ONCE, then a compact per-route table lists only what actually differs
 * (reflector, neighbor, best, local-pref, MED, next hop, destination, AS path). Far more
 * scannable than repeating a full detail block per route.
 */
const RouteGroupDetail: React.FC<{ rows: RouteRow[]; direction: 'INBOUND' | 'OUTBOUND' }> = ({ rows: allRows, direction }) => {
  // A withdraw tombstone survives next to a live announcement when the two key on
  // different route tables (e.g. the withdraw targeted an unresolved tag), so a
  // prefix that IS announced was still listing a ghost row with no neighbor and no
  // next hop — and dragging the shared attributes to "2 values". While the prefix
  // has any live route, the tombstones are history and stay out of this panel.
  const rows = React.useMemo(
    () => (allRows.some((r) => !r.withdrawn) ? allRows.filter((r) => !r.withdrawn) : allRows),
    [allRows]
  );
  const destHeader = direction === 'OUTBOUND' ? 'Subnet' : 'Route Table';
  const showVpnColumns = rows.some((r) => r.rd || (r.rt && r.rt.length > 0));
  const destOf = (r: RouteRow) => (direction === 'OUTBOUND' ? r.subnetId ?? r.routeTableId : r.routeTableId) || '—';

  // A single neighbor announcing the same route into several route tables shows up as one
  // RouteRow per route table (same reflector, neighbor, best, LP, MED, origin, next hop, AS
  // path). Collapse those into one row and merge the differing destinations into a multi-chip
  // cell, so the reader sees each (reflector, neighbor, path) once instead of a duplicated stack.
  //
  // `origin` is part of the key: it is a per-path attribute, so two candidates that differ only
  // by origin are genuinely different paths and must stay on separate lines (CTX-322).
  const condensed = React.useMemo(() => {
    const groups = new Map<string, { row: RouteRow; dests: string[]; merged: RouteRow[] }>();
    for (const row of rows) {
      const key = [
        row.reflectorId,
        row.neighborIp,
        row.withdrawn,
        row.isBestPath,
        row.localPref,
        row.med,
        row.origin,
        row.nextHop ?? '',
        row.asPath.join(','),
        row.rd ?? '',
        (row.rt || []).join(',')
      ].join('|');
      const existing = groups.get(key);
      if (existing) {
        existing.dests.push(destOf(row));
        existing.merged.push(row);
      } else {
        groups.set(key, { row, dests: [destOf(row)], merged: [row] });
      }
    }
    return Array.from(groups.values());
  }, [rows, direction]);

  return (
    <div className="py-2 text-xs text-semantic-text-secondary">
      <div className="grid grid-cols-2 gap-x-8 gap-y-1 md:grid-cols-4">
        <SharedField label="Direction" values={rows.map((r) => r.direction)} />
        <SharedField label="Family" values={rows.map((r) => r.family)} />
        <SharedField label="Origin" values={rows.map((r) => r.origin)} />
        <SharedField label="Status" values={rows.map((r) => r.status)} />
        <SharedField label="Cluster" values={rows.map((r) => r.clusterName || r.clusterId)} />
        <SharedField label="RD" values={rows.map((r) => r.rd)} />
        <SharedField label="RT" values={rows.map((r) => r.rt)} />
        <SharedField
          label="Received at"
          values={rows.map((r) => parseWireDate(r.receivedAt)?.toLocaleString() ?? null)}
        />
        <SharedField
          label="Tags"
          values={rows.map((r) => formatTagAttribution(r.tagAttribution).join(' · ') || null)}
        />
        <SharedField
          label="Decision"
          values={rows.map((r) => decisionLabel(r.decisionReason))}
          className="col-span-2 md:col-span-4"
          title={dedupeValues(rows.map((r) => r.decisionReason)).join(', ') || undefined}
        />
        <GroupNotes label="Cloud error" notes={notesByText(rows, cloudErrorOf, destOf)} />
        <GroupNotes label="Next hop" notes={notesByText(rows, describeNextHopIssue, destOf)} />
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-semantic-border-secondary text-left">
              <th className="px-2 py-1 font-semibold">Reflector</th>
              <th className="px-2 py-1 font-semibold">Neighbor IP</th>
              <th className="px-2 py-1 font-semibold">Best</th>
              <th className="px-2 py-1 font-semibold">LP</th>
              <th className="px-2 py-1 font-semibold">MED</th>
              <th className="px-2 py-1 font-semibold">Origin</th>
              <th className="px-2 py-1 font-semibold">Next Hop</th>
              <th className="px-2 py-1 font-semibold">{destHeader}</th>
              {showVpnColumns && <th className="px-2 py-1 font-semibold">RD</th>}
              {showVpnColumns && <th className="px-2 py-1 font-semibold">RT</th>}
              <th className="px-2 py-1 font-semibold">Cloud</th>
              <th className="px-2 py-1 font-semibold">AS Path</th>
              <th className="px-2 py-1 font-semibold">Received at</th>
            </tr>
          </thead>
          <tbody>
            {condensed.map(({ row, dests, merged }) => (
              <tr key={row.id} className="border-b border-semantic-border-secondary/50">
                <td className="whitespace-nowrap px-2 py-1 font-medium text-semantic-text-primary">
                  <div className="flex flex-col leading-tight">
                    <span>{row.reflectorName || row.reflectorId || '—'}</span>
                    {formatClusterRole(row.reflectorRole) && (
                      <span className="mt-0.5 text-[9px] font-normal uppercase tracking-wide text-semantic-text-tertiary">
                        {formatClusterRole(row.reflectorRole)}
                      </span>
                    )}
                    {row.withdrawn && (
                      <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-semantic-text-danger">
                        Withdraw
                      </span>
                    )}
                  </div>
                </td>
                <td className="whitespace-nowrap px-2 py-1">{row.neighborIp}</td>
                <td className="px-2 py-1">
                  {row.isBestPath ? (
                    <GenericTag label="Best" variant="success" size="small" />
                  ) : (
                    <GenericTag label="Alt" variant="default" size="small" />
                  )}
                </td>
                <td className="px-2 py-1 tabular-nums">{row.localPref}</td>
                <td className="px-2 py-1 tabular-nums">{row.med}</td>
                <td className="whitespace-nowrap px-2 py-1">{row.origin || '—'}</td>
                <td className="max-w-[160px] truncate px-2 py-1" title={row.nextHop ?? undefined}>{row.nextHop || '—'}</td>
                <td className="px-2 py-1"><MultiChipCell values={dedupeValues(dests)} maxVisible={2} truncateLabels={false} /></td>
                {showVpnColumns && (
                  <td className="whitespace-nowrap px-2 py-1">{row.rd || '—'}</td>
                )}
                {showVpnColumns && (
                  <td className="max-w-[150px] truncate px-2 py-1" title={(row.rt || []).join(', ')}>
                    {(row.rt || []).join(', ') || '—'}
                  </td>
                )}
                <td className="px-2 py-1"><CloudBadge health={worstCloudRouteHealth(merged)} /></td>
                <td className="max-w-[160px] truncate px-2 py-1" title={formatAsPath(row.asPath)}>{formatAsPath(row.asPath)}</td>
                <td className="whitespace-nowrap px-2 py-1">{formatReceivedAt(row.receivedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Navigable route-announcement table backed by the shared normalized route-row model.
 * Rows sharing the same prefix are grouped into one visual row with a multiplicity badge
 * (scope C.1); the group's single-value columns show the best-path route (or the first
 * route when none is marked best). Expanding a group reveals every underlying route's full
 * detail so differing reflector/neighbor/family/destination data is never lost or merged.
 * Column sorting + pagination come from GenericTable (client-side; only one page of groups
 * renders at a time, which keeps it responsive on production-scale datasets).
 */
export const BgpRouteTable: React.FC<BgpRouteTableProps> = ({
  rows,
  direction,
  showReflectorColumn,
  loading = false
}) => {
  const groups = React.useMemo(() => groupRoutesByPrefix(rows), [rows]);
  const destinationHeader = direction === 'OUTBOUND' ? 'Subnet' : 'Route Table';

  const columns: GenericTableColumn<RouteRowGroup>[] = [
    {
      id: 'prefix',
      header: 'Prefix',
      sortAccessor: (g) => g.prefix,
      width: '132px',
      renderCell: (g) => (
        <div className="flex flex-col leading-tight">
          <span className={g.representative.withdrawn ? 'line-through decoration-1 text-semantic-text-secondary' : ''}>
            {g.prefix}
          </span>
          {g.representative.withdrawn && (
            <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-semantic-text-danger">
              Withdraw
            </span>
          )}
        </div>
      )
    },
    {
      id: 'family',
      header: 'Family',
      sortAccessor: (g) => g.representative.family,
      width: '88px',
      renderCell: (g) => (
        <TagCell>
          <GenericTag label={routeFamilyLabel(g.representative.family)} variant="neutral" size="small" />
        </TagCell>
      )
    },
    {
      id: 'neighborIp',
      header: 'Neighbor IP',
      sortAccessor: (g) => g.representative.neighborIp,
      width: '140px',
      renderCell: (g) => <span>{g.representative.neighborIp}</span>
    },
    {
      id: 'neighborAs',
      header: 'Neighbor ASN',
      sortAccessor: (g) => g.representative.neighborAs ?? '',
      width: '104px',
      renderCell: (g) => dash(g.representative.neighborAs)
    },
    ...(showReflectorColumn
      ? [
          {
            id: 'reflector',
            header: 'Reflector',
            // Every member that announced this prefix, not just the group's representative: in a
            // cluster the same prefix is routinely discovered by both members, and showing only
            // the best-path row's reflector hid the other one's contribution entirely.
            sortAccessor: (g: RouteRowGroup) => reflectorsOf(g).join(', '),
            width: '200px',
            renderCell: (g: RouteRowGroup) => <MultiChipCell values={reflectorsOf(g)} maxVisible={1} singleRow />
          } as GenericTableColumn<RouteRowGroup>
        ]
      : []),
    {
      id: 'best',
      header: 'Best',
      sortAccessor: (g) => (g.representative.isBestPath ? 1 : 0),
      width: '90px',
      // Best is strictly the path-selection status. Withdraw is surfaced by the strikethrough +
      // "Withdraw" label in the Prefix column and the dedicated Withdraw column, so it must not
      // hijack this column.
      renderCell: (g) => (
        <TagCell>
          {g.representative.isBestPath ? (
            <GenericTag label="Best" variant="success" size="small" />
          ) : (
            <GenericTag label="Alt" variant="default" size="small" />
          )}
        </TagCell>
      )
    },
    {
      id: 'cloud',
      header: 'Cloud',
      // Sorts by severity, not alphabetically: the point of the column is that the
      // broken routes surface first. Worst state across the group, since a prefix can
      // span several route tables with different outcomes.
      sortAccessor: (g) => CLOUD_HEALTH_SEVERITY.indexOf(worstCloudRouteHealth(g.rows).state),
      width: '112px',
      renderCell: (g) => {
        const health = worstCloudRouteHealth(g.rows);
        return (
          <TagCell title={health.detail}>
            {health.state === 'unknown' ? (
              <span className="text-semantic-text-tertiary">—</span>
            ) : (
              <GenericTag label={health.label} variant={health.variant} size="small" />
            )}
          </TagCell>
        );
      }
    },
    {
      id: 'localPref',
      header: 'Local Pref',
      sortAccessor: (g) => g.representative.localPref,
      width: '84px',
      renderCell: (g) => <span>{g.representative.localPref}</span>
    },
    {
      id: 'med',
      header: 'MED',
      sortAccessor: (g) => g.representative.med,
      width: '64px',
      renderCell: (g) => <span>{g.representative.med}</span>
    },
    {
      id: 'nextHop',
      header: 'Next Hop',
      sortAccessor: (g) => g.representative.nextHop ?? '',
      width: '170px',
      renderCell: (g) => <MultiChipCell values={dedupeValues(g.rows.map((r) => r.nextHop))} maxVisible={1} singleRow />
    },
    {
      id: 'vpc',
      header: 'VPC',
      sortAccessor: (g) => g.representative.vpc,
      width: '130px',
      wrapMode: 'truncate',
      renderCell: (g) => <span title={g.representative.vpc}>{g.representative.vpc || '—'}</span>
    },
    {
      id: 'destination',
      header: destinationHeader,
      sortAccessor: (g) => (direction === 'OUTBOUND' ? g.representative.subnetId ?? g.representative.routeTableId : g.representative.routeTableId),
      width: '170px',
      renderCell: (g) => (
        <MultiChipCell
          values={dedupeValues(
            g.rows.map((r) => (direction === 'OUTBOUND' ? r.subnetId ?? r.routeTableId : r.routeTableId))
          )}
          maxVisible={1}
          singleRow
        />
      )
    },
    {
      id: 'tags',
      header: 'Tags',
      // Untagged groups sort together, so scanning for tag-based destinations is one click.
      sortAccessor: (g) => discoveryTagLabels(g.rows).join(' '),
      width: '160px',
      renderCell: (g) => {
        const labels = discoveryTagLabels(g.rows);
        return labels.length > 0 ? <MultiChipCell values={labels} maxVisible={1} singleRow /> : dash(null);
      }
    },
    {
      id: 'withdraw',
      header: 'Withdraw',
      sortAccessor: (g) => (g.representative.withdrawn ? 1 : 0),
      width: '130px',
      renderCell: (g) =>
        g.representative.withdrawn ? (
          <GenericTag
            label="Withdrawn"
            variant="error"
            size="small"
            tooltip={g.representative.withdrawnNeighborIp ? `Withdrawn by ${g.representative.withdrawnNeighborIp}` : undefined}
          />
        ) : (
          dash(g.representative.withdrawnNeighborIp)
        )
    },
    {
      id: 'asPath',
      header: 'AS Path',
      sortAccessor: (g) => g.representative.asPath.length,
      width: '140px',
      wrapMode: 'truncate',
      renderCell: (g) => <span title={formatAsPath(g.representative.asPath)}>{formatAsPath(g.representative.asPath)}</span>
    },
    {
      id: 'actions',
      header: 'Actions',
      // Pinned right so the expand control stays reachable when the table scrolls horizontally.
      fixed: 'right',
      width: '84px',
      renderCell: (_g, rowIndex, toggleRowExpand, isExpanded) => (
        <div className="flex justify-center">
          <button
            type="button"
            aria-label={isExpanded ? 'Collapse route details' : 'Expand route details'}
            aria-expanded={isExpanded}
            onClick={(e) => {
              e.stopPropagation();
              toggleRowExpand?.(rowIndex);
            }}
            className={`inline-flex h-6 w-6 items-center justify-center rounded-md border transition-colors ${
              isExpanded
                ? 'border-semantic-border-brand-primary bg-semantic-bg-brand-secondary text-semantic-text-brand'
                : 'border-semantic-border-secondary bg-semantic-bg-primary text-semantic-text-secondary hover:bg-semantic-state-active hover:text-semantic-text-primary'
            }`}
          >
            <ChevronDown size={14} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
      )
    }
  ];

  return (
    <GenericTable<RouteRowGroup>
      columns={columns}
      data={groups}
      loading={loading}
      rowKey={(g) => g.prefix}
      initialRowsPerPage={50}
      rowsPerPageOptions={[25, 50, 100, 200]}
      maxTableBodyHeight="100%"
      // Fit within the viewport on large screens (no forced min-w-max); scroll only on narrower
      // ones. Combined with the tighter widths + condensed RT/Next-Hop cells this keeps the
      // common wide-screen layout scroll-free (CTX-74).
      horizontalScroll="lg-fit"
      expandable
      showExpandColumn={false}
      renderExpanded={(g) =>
        g.rows.length === 1 ? (
          <RouteDetailBlock row={g.rows[0]} direction={direction} />
        ) : (
          <RouteGroupDetail rows={g.rows} direction={direction} />
        )
      }
    />
  );
};

export default BgpRouteTable;
